import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Container } from '@sbclite/core';
import { api } from '../services/api';
import { containerQueryKeys } from './useContainers';
import { useAuth } from '../contexts/AuthContext';

interface ListEnvelope<T> {
  readonly success: boolean;
  readonly data: readonly T[];
}

interface ItemEnvelope<T> {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
}

export const containerProfileKeys = {
  profiles: ['container-profiles'] as const,
  profile: (id: string | undefined) => ['container-profiles', id] as const,
  containers: ['container-profiles', 'containers'] as const,
};

export function useContainerProfiles() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...containerProfileKeys.profiles, user?.id ?? 'anonymous'] as const,
    queryFn: async () => {
      const response = await api.get<ListEnvelope<Container.ProfileDetailInfo>>('/api/container-profiles');
      return response.data.data;
    },
    enabled: user?.role === 'admin',
  });
}

export function useContainerProfile(id: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...containerProfileKeys.profile(id), user?.id ?? 'anonymous'] as const,
    queryFn: async () => {
      const response = await api.get<ItemEnvelope<Container.ProfileDetailInfo>>(`/api/container-profiles/${id}`);
      return response.data.data;
    },
    enabled: !!id && user?.role === 'admin',
  });
}

export function useContainerAssignmentStatuses() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...containerProfileKeys.containers, user?.id ?? 'anonymous'] as const,
    queryFn: async () => {
      const response = await api.get<ListEnvelope<Container.ContainerAssignmentStatusInfo>>('/api/container-profiles/containers');
      return response.data.data;
    },
    refetchInterval: 15000,
    enabled: user?.role === 'admin',
  });
}

export function useCreateContainerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Container.ProfileCreateInput) => {
      const response = await api.post<ItemEnvelope<Container.ProfileDetailInfo>>('/api/container-profiles', input);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles }),
  });
}

export function useUpdateContainerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { readonly id: string; readonly input: Container.ProfileUpdateInput }) => {
      const response = await api.patch<ItemEnvelope<Container.ProfileDetailInfo>>(`/api/container-profiles/${id}`, input);
      return response.data;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.containers });
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles });
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profile(variables.id) });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}

export function useDeleteContainerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<ItemEnvelope<{ readonly id: string }>>(`/api/container-profiles/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles });
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.containers });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}

export function useAssignContainerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, containerId, strictMode }: { readonly profileId: string; readonly containerId: string; readonly strictMode?: boolean }) => {
      const response = await api.post<ItemEnvelope<Container.ProfileAssignmentInfo>>(`/api/container-profiles/${profileId}/assignments`, {
        containerId,
        strictMode: strictMode ?? true,
      });
      return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles }),
        queryClient.invalidateQueries({ queryKey: containerProfileKeys.containers }),
        queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot }),
      ]);
    },
  });
}

export function useUnassignContainerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, containerId }: { readonly profileId: string; readonly containerId: string }) => {
      const response = await api.delete<ItemEnvelope<{ readonly id: string; readonly containerId: string }>>(`/api/container-profiles/${profileId}/assignments/${encodeURIComponent(containerId)}`);
      return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles }),
        queryClient.invalidateQueries({ queryKey: containerProfileKeys.containers }),
        queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot }),
      ]);
    },
  });
}

export function useLinkContainerProfileInterface() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, sipInterfaceId }: { readonly profileId: string; readonly sipInterfaceId: string }) => {
      const response = await api.post<ItemEnvelope<Container.ProfileDetailInfo>>(`/api/container-profiles/${profileId}/interfaces/${sipInterfaceId}`);
      return response.data;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles });
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profile(variables.profileId) });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}

export function useUnlinkContainerProfileInterface() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, sipInterfaceId }: { readonly profileId: string; readonly sipInterfaceId: string }) => {
      const response = await api.delete<ItemEnvelope<Container.ProfileDetailInfo>>(`/api/container-profiles/${profileId}/interfaces/${sipInterfaceId}`);
      return response.data;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profiles });
      queryClient.invalidateQueries({ queryKey: containerProfileKeys.profile(variables.profileId) });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}
